import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Loader2, Plus, CheckCircle2, Circle, Clock, AlertTriangle } from "lucide-react";
import { format, addDays, subDays, isToday, isTomorrow } from "date-fns";
import { toast } from "sonner";
import { API } from "../../App";
import { useTimezone } from "../../contexts/TimezoneContext";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

function parseTimeStr(s) {
  if (!s) return null;
  const ampm = String(s).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    if (ampm[3].toUpperCase() === "PM" && h < 12) h += 12;
    if (ampm[3].toUpperCase() === "AM" && h === 12) h = 0;
    return h * 60 + parseInt(ampm[2], 10);
  }
  const h24 = String(s).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (h24) return parseInt(h24[1], 10) * 60 + parseInt(h24[2], 10);
  return null;
}

function displayTime(s) {
  const mins = parseTimeStr(s);
  if (mins === null) return "";
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/** Parse "YYYY-MM-DD" as a LOCAL date (avoid UTC shift). */
function localDate(dKey) {
  const [y, m, d] = String(dKey).split("-").map(Number);
  return new Date(y, m - 1, d);
}

function dateHeaderLabel(dKey) {
  const d = localDate(dKey);
  if (isToday(d)) return "Today";
  if (isTomorrow(d)) return "Tomorrow";
  return format(d, "EEE, MMM d");
}

const DispatchTasksPanel = ({ open, onClose, tags, assigneeNames, onEditTask, onCreateTask, refreshKey, onChanged }) => {
  const { getToday } = useTimezone();
  const [tab, setTab] = useState("open");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const tagMap = useMemo(() => Object.fromEntries((tags || []).map((t) => [t.tag_id, t])), [tags]);
  const todayKey = format(getToday(), "yyyy-MM-dd");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const start = format(subDays(getToday(), 30), "yyyy-MM-dd");
      const end = format(addDays(getToday(), 90), "yyyy-MM-dd");
      const res = await axios.get(`${API}/dispatch/tasks?start_date=${start}&end_date=${end}`, getAuthConfig());
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (open) fetchAll(); }, [open, refreshKey, fetchAll]);

  const toggleDone = async (task) => {
    setTogglingId(task.task_id);
    try {
      await axios.put(`${API}/dispatch/tasks/${task.task_id}`, {
        ...task,
        status: task.status === "done" ? "open" : "done",
      }, getAuthConfig());
      await fetchAll();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update task");
    } finally {
      setTogglingId(null);
    }
  };

  const visible = tasks.filter((t) => (tab === "open" ? t.status !== "done" : t.status === "done"));
  const byDate = useMemo(() => {
    const out = {};
    visible.forEach((t) => {
      const k = String(t.scheduled_date || "").slice(0, 10);
      if (!out[k]) out[k] = [];
      out[k].push(t);
    });
    Object.values(out).forEach((arr) =>
      arr.sort((a, b) => (parseTimeStr(a.scheduled_time) ?? 0) - (parseTimeStr(b.scheduled_time) ?? 0))
    );
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b));
  }, [visible]);

  const openTodayCount = tasks.filter((t) => t.status !== "done" && t.scheduled_date === todayKey).length;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md bg-card border-theme overflow-y-auto" data-testid="dispatch-tasks-panel">
        <SheetHeader className="mb-3">
          <SheetTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
            Dispatcher Tasks
            {openTodayCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-[#FFCC00] text-black text-[11px] font-bold">{openTodayCount} today</span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex items-center gap-2 mb-3">
          <div className="flex bg-theme-surface rounded-lg p-1 flex-1">
            <button onClick={() => setTab("open")}
              className={`flex-1 h-7 rounded text-xs font-medium transition-colors ${tab === "open" ? "bg-[#FFCC00] text-black" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="tasks-panel-tab-open">
              Open
            </button>
            <button onClick={() => setTab("done")}
              className={`flex-1 h-7 rounded text-xs font-medium transition-colors ${tab === "done" ? "bg-[#FFCC00] text-black" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="tasks-panel-tab-done">
              Done
            </button>
          </div>
          <Button size="sm" onClick={onCreateTask} className="btn-primary h-8 px-3 text-xs" data-testid="tasks-panel-new-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Task
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : byDate.length === 0 ? (
          <div className="text-center py-12 text-sm text-muted-foreground" data-testid="tasks-panel-empty">
            {tab === "open" ? "No open tasks — all caught up!" : "No completed tasks yet."}
          </div>
        ) : (
          <div className="space-y-4 pb-6">
            {byDate.map(([dKey, items]) => {
              const overdue = tab === "open" && dKey < todayKey;
              return (
                <div key={dKey}>
                  <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${overdue ? "text-red-400" : "text-muted-foreground"}`}>
                    {overdue && <AlertTriangle className="w-3 h-3" />}
                    {dateHeaderLabel(dKey)}{overdue ? " · overdue" : ""}
                  </div>
                  <div className="space-y-1.5">
                    {items.map((task) => {
                      const done = task.status === "done";
                      const taskTags = (task.tag_ids || []).map((id) => tagMap[id]).filter(Boolean);
                      return (
                        <div key={task.task_id}
                          className={`flex items-start gap-2.5 p-2.5 rounded-lg border border-theme bg-theme-surface/50 hover:bg-theme-hover cursor-pointer transition-colors ${done ? "opacity-60" : ""}`}
                          onClick={() => onEditTask(task)}
                          data-testid={`tasks-panel-item-${task.task_id}`}
                        >
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleDone(task); }}
                            className="mt-0.5 text-muted-foreground hover:text-green-500 transition-colors flex-shrink-0"
                            data-testid={`tasks-panel-toggle-${task.task_id}`}
                          >
                            {togglingId === task.task_id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                              done ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Circle className="w-4 h-4" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className={`text-[13px] font-medium text-foreground truncate ${done ? "line-through" : ""}`}>{task.title}</div>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {taskTags.map((t) => (
                                <span key={t.tag_id} className="px-1.5 rounded text-[9px] font-bold text-white" style={{ backgroundColor: t.color }}>{t.name}</span>
                              ))}
                              {task.scheduled_time && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                  <Clock className="w-2.5 h-2.5" /> {displayTime(task.scheduled_time)}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground">
                                {assigneeNames[task.assigned_to] || "Office"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default DispatchTasksPanel;
